import KeycloakService from './keycloakService';
import { TypeNotification, useGlobalStore } from '@/store/globalStore';
// @ts-ignore
import SrhUiI18nService from 'srh-ui/dist/services/i18n';
import { HTTPMethods, callApi } from '@/utils/api';
import { UseStoreUser } from '@/store/userStore';
import { getApiUrl } from '@/utils/url';
import type {
  UserResponseDto,
  CreateUserDTO,
  UpdateUserDTO,
  PreferencesDto,
  PreferencesResponse,
} from '@/types/api';

export default {
  /** GET /user/:id → UserResponseDto */
  async loadUserData() {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const userStore = UseStoreUser();
    const globalStore = useGlobalStore();
    const userId = userStore.user.id;

    try {
      await KeycloakService.CallUpdate().then(async () => {
        const config = {
          'Content-Type': 'application/json',
        };

        await callApi({
          path: `${getApiUrl()}/user/${userId}`,
          method: HTTPMethods.GET,
          options: config,
          successCallback: (result: UserResponseDto) => {
            userStore.loadUserFavorite(result);
          },
        });
      });
    } catch (e: unknown) {
      const status =
        (e as { response?: { status?: number }; status?: number })?.response
          ?.status ?? (e as { status?: number })?.status;
      if (status === 404) return;
      globalStore.showNotification(
        TypeNotification.ERROR,
        t('errors.unknown'),
        t('errors.title')
      );
    }
  },

  /** POST /add-user with CreateUserDTO → UserResponseDto */
  async createUser(
    userData: CreateUserDTO,
    onSuccess?: (result: UserResponseDto) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();
    try {
      await KeycloakService.CallUpdate().then(async () => {
        const config = {
          'Content-Type': 'application/json',
        };

        await callApi({
          path: `${getApiUrl()}/add-user`,
          method: HTTPMethods.POST,
          options: config,
          body: userData,
          successCallback: (result: UserResponseDto) => {
            onSuccess?.(result);
          },
          errorMessage: t('errors.user'),
        });
      });
    } catch (e: unknown) {
      onError?.(e);
      const status =
        (e as { response?: { status?: number }; status?: number })?.response
          ?.status ?? (e as { status?: number })?.status;
      if (status === 404) return;
      if (!onError) {
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
      }
    }
  },

  /** PUT /:id with UpdateUserDTO (favorite, username) → UserResponseDto */
  async updateFavorite(
    userData: { id: string } & UpdateUserDTO,
    onSuccess?: (result: UserResponseDto) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();
    const userStore = UseStoreUser();
    const id = userData.id;
    try {
      await KeycloakService.CallUpdate().then(async () => {
        const config = {
          'Content-Type': 'application/json',
        };
        const body: UpdateUserDTO = {
          favorite: userData.favorite,
          username: userData.username,
        };
        await callApi({
          path: `${getApiUrl()}/${id}`,
          method: HTTPMethods.PUT,
          options: config,
          body,
          globalLoader: false,
          successCallback: (result: UserResponseDto) => {
            userStore.loadUserFavorite(result);
            onSuccess?.(result);
          },
          errorMessage: t('errors.user'),
        });
      });
    } catch (e: unknown) {
      onError?.(e);
      if (!onError) {
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
      }
    }
  },

  /** PUT /preferences/:userId with PreferencesDto → PreferencesResponse */
  async updatePreferences(
    userId: string,
    preferences: PreferencesDto,
    onSuccess?: (result: PreferencesResponse) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate();
      const config = {
        'Content-Type': 'application/json',
      };

      await callApi({
        path: `${getApiUrl()}/preferences/${userId}`,
        method: HTTPMethods.PUT,
        options: config,
        body: preferences,
        successCallback: (result: PreferencesResponse) => {
          onSuccess?.(result);
          i18n.setLocale(result.language);
          const { t: t2 } = i18n.getI18nInstance().global;
          globalStore.showNotification(
            TypeNotification.SUCCESS,
            t2('notifications.preferencesUpdatedSuccessfully')
          );
        },
        errorMessage: t('errors.preferences'),
      });
    } catch (e: unknown) {
      onError?.(e);
      if (!onError) {
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
      }
    }
  },
};
